import { ASSIGNEE_PRINCIPAL_FIELD, ASSIGNEE_TEAM_FIELD } from "./build-where";
import { parseAssigneePrincipalValue } from "./composite-values";
import type { FilterState, FilterValue } from "./types";

/**
 * Bring a stored FilterState up to the field set the panel edits today:
 * Assignee (people) and Teams (the project's Teams property).
 *
 * Two retired shapes hold a team whose MEMBERS were assigned — a standalone
 * `assigneeTeamIds` row, and the `{ userIds, teamIds }` Assignee value from
 * when one control took people or teams. The panel no longer offers that
 * question (a team's work is what carries the team), so each team half
 * becomes a Teams row — the closest reading, and never an applied filter the
 * panel cannot show. The people half stays an Assignee row.
 *
 * A fused value's halves ORed; split, they are two rows in one block, which
 * AND. Flipping the block to OR would OR every other filter in it too, so AND
 * is the only reading available.
 *
 * A block that already had a Teams row gets ONE Teams row, not two: the panel
 * reads the first row of a field only, so a second would narrow the query
 * with no chip to show or clear it. The `hasSome` rows merge into one list,
 * which is broader than two ANDed rows; a visible filter is worth that.
 *
 * Pure, and applied where a stored state is SEEDED (`useFilters`). States that
 * go straight to a query read the same way: `processFilter` maps the retired
 * shapes onto `teamIds` too.
 */
export function migrateStoredFilterState(state: FilterState): FilterState {
  let changed = false;

  const blocks = state.blocks.map((block) => {
    const filters: FilterValue[] = [];

    for (const filter of block.filters) {
      if (filter.field === ASSIGNEE_TEAM_FIELD) {
        changed = true;
        filters.push({ ...filter, field: "teamIds" });
        continue;
      }

      const fused =
        filter.field === ASSIGNEE_PRINCIPAL_FIELD &&
        !!filter.value &&
        typeof filter.value === "object" &&
        !Array.isArray(filter.value);
      if (!fused) {
        filters.push(filter);
        continue;
      }

      changed = true;
      const { userIds, teamIds } = parseAssigneePrincipalValue(filter.value);
      if (userIds.length > 0) {
        filters.push({ field: ASSIGNEE_PRINCIPAL_FIELD, operator: "hasSome", value: userIds });
      }
      if (teamIds.length > 0) {
        filters.push({ field: "teamIds", operator: "hasSome", value: teamIds });
      }
    }

    return { ...block, filters: mergeTeamRows(filters) };
  });

  if (!changed) return state;
  return { ...state, blocks: blocks.filter((b) => b.filters.length > 0) };
}

/** Fold every `teamIds` `hasSome` row of a block into the first one. */
function mergeTeamRows(filters: FilterValue[]): FilterValue[] {
  const isTeamList = (f: FilterValue) =>
    f.field === "teamIds" && f.operator === "hasSome" && Array.isArray(f.value);
  const first = filters.findIndex(isTeamList);
  if (first === -1 || filters.filter(isTeamList).length < 2) return filters;
  const ids = new Set<string>();
  for (const f of filters) {
    if (isTeamList(f)) for (const id of f.value as string[]) ids.add(id);
  }
  return filters.flatMap((f, i) => {
    if (i === first) return [{ ...f, value: [...ids] }];
    return isTeamList(f) ? [] : [f];
  });
}

const EMPTY_STORED_STATE: FilterState = { blocks: [], logic: "AND" };

/**
 * Read a stored filter blob (a saved filter's `filters` / `advancedFilters`,
 * opaque JSON on the wire) as a FilterState. Anything that is not the shape —
 * an empty object from a row saved with one editor untouched, a blob a newer
 * client wrote — reads as no filters rather than a crash in the panel.
 */
export function parseStoredFilterState(value: unknown): FilterState {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return EMPTY_STORED_STATE;
  }
  const { blocks, logic } = value as { blocks?: unknown; logic?: unknown };
  if (!Array.isArray(blocks)) return EMPTY_STORED_STATE;
  return {
    blocks: blocks as FilterState["blocks"],
    logic: logic === "OR" ? "OR" : "AND",
  };
}
